"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useStyles = exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireDefault(require("react"));
var _createStyles = require("../styles/createStyles");
var _utilities = require("../utilities");
var _useEventCallback = _interopRequireDefault(require("../utilities/useEventCallback"));
var _useForkRef = _interopRequireDefault(require("../utilities/useForkRef"));
const _excluded = ["action", "buttonRef", "children", "classes", "className", "component", "disabled", "onBlur", "onClick", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "onMouseLeave", "onMouseUp", "onTouchEnd", "onTouchMove", "onTouchStart", "onDragLeave", "tabIndex", "type"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const useStyles = exports.useStyles = (0, _createStyles.createStyles)(_ref => {
  let {
    theme
  } = _ref;
  return {
    /* Styles applied to the root element. */
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      // Remove grey highlight
      WebkitTapHighlightColor: 'transparent',
      backgroundColor: 'transparent',
      // Reset default value
      border: 0,
      margin: 0,
      // Remove the margin in Safari
      borderRadius: 0,
      padding: 0,
      // Remove the padding in Firefox
      cursor: 'pointer',
      userSelect: 'none',
      verticalAlign: 'middle',
      MozAppearance: 'none',
      // Reset
      WebkitAppearance: 'none',
      // Reset
      fontSize: '100%',
      textDecoration: 'none',
      // So we take precedent over the style of a native <a /> element.
      color: 'inherit',
      '&:focus-visible': _objectSpread({}, (0, _utilities.addFocusRingStyles)(theme, {})),
      '&[disabled]': {
        pointerEvents: 'none',
        // Disable link interactions
        cursor: 'default'
      }
    },
    /* Pseudo-class applied to the root element if `disabled={true}`. */
    disabled: {
      opacity: theme.busbud.styles.component.button.stateDisabled.opacity
    }
  };
});
/**
 * **❗️ DEPRECATED**: Use the new `UnstyledButton` component from `@busbud/horizon`:
 *
 * ```tsx
 * import { UnstyledButton } from "@busbud/horizon";
 * ```
 * [Storybook](https://busbud.github.io/horizon-web-ui-kit/?path=/docs/forms-unstyledbutton)
 *
 * @deprecated
 */
const ButtonBase = /*#__PURE__*/_react.default.forwardRef(function ButtonBase(props, ref) {
  const {
      action,
      buttonRef: buttonRefProp,
      children,
      classes: classesOverrides,
      className: classNameProp,
      component = 'button',
      disabled,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseLeave,
      onMouseUp,
      onTouchEnd,
      onTouchMove,
      onTouchStart,
      onDragLeave,
      tabIndex = 0,
      type = 'button'
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded);
  const {
    classes,
    cx
  } = useStyles({
    classesOverrides
  });
  const buttonRef = _react.default.useRef(null);
  function getButtonNode() {
    // #StrictMode ready
    return buttonRef.current;
  }
  const handleMouseDown = onMouseDown;
  const handleDragLeave = onDragLeave;
  const handleMouseUp = onMouseUp;
  const handleMouseLeave = event => {
    if (onMouseLeave) {
      onMouseLeave(event);
    }
  };
  const handleTouchStart = onTouchStart;
  const handleTouchEnd = onTouchEnd;
  const handleTouchMove = onTouchMove;
  const handleBlur = event => {
    if (onBlur) {
      onBlur(event);
    }
  };
  const handleFocus = (0, _useEventCallback.default)(event => {
    if (disabled) {
      return;
    }

    // Fix for https://github.com/facebook/react/issues/7769
    if (!buttonRef.current) {
      buttonRef.current = event.currentTarget;
    }
    if (onFocus) {
      onFocus(event);
    }
  });

  /**
   * IE 11 shim for https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/repeat
   */
  const keydownRef = _react.default.useRef(false);
  const handleKeyDown = (0, _useEventCallback.default)(event => {
    // Check if key is already down to avoid repeats being counted as multiple activations
    if (!keydownRef.current && event.key === ' ') {
      keydownRef.current = true;
      event.persist();
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
    const button = getButtonNode();
    // Keyboard accessibility for non interactive elements
    if (event.target === event.currentTarget && component && component !== 'button' && (event.key === ' ' || event.key === 'Enter') && !(button?.tagName === 'A' && button.href)) {
      event.preventDefault();
      if (onClick) {
        onClick(event);
      }
    }
  });
  const handleKeyUp = (0, _useEventCallback.default)(event => {
    if (event.key === ' ') {
      keydownRef.current = false;
      event.persist();
    }
    if (onKeyUp) {
      onKeyUp(event);
    }
  });
  const className = cx(classes.root, {
    [classes.disabled]: disabled
  }, classNameProp);
  let ComponentProp = component;
  if (ComponentProp === 'button' && other.href) {
    ComponentProp = 'a';
  }
  const buttonProps = {};
  if (ComponentProp === 'button') {
    buttonProps.type = type;
    buttonProps.disabled = disabled;
  } else {
    if (ComponentProp !== 'a' || !other.href) {
      buttonProps.role = 'button';
    }
    buttonProps['aria-disabled'] = disabled;
  }

  // @ts-ignore TS issue around `buttonRefProp` may be able to be fixed if these are wrapped
  // in a `if (buttonRefProp)` but not sure of the implications of that yet
  const handleUserRef = (0, _useForkRef.default)(buttonRefProp, ref);
  const handleRef = (0, _useForkRef.default)(handleUserRef, buttonRef);
  return (
    /*#__PURE__*/
    // @ts-ignore TS2322
    _react.default.createElement(ComponentProp
    // @ts-ignore TS2590
    , (0, _extends2.default)({
      className: className,
      onBlur: handleBlur,
      onClick: onClick,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onMouseDown: handleMouseDown,
      onMouseLeave: handleMouseLeave,
      onMouseUp: handleMouseUp,
      onDragLeave: handleDragLeave,
      onTouchEnd: handleTouchEnd,
      onTouchMove: handleTouchMove,
      onTouchStart: handleTouchStart,
      ref: handleRef,
      tabIndex: disabled ? -1 : tabIndex
    }, buttonProps, other), children)
  );
});
var _default = exports.default = ButtonBase;